import React from 'react';
import { Home, Trees, Building2 } from 'lucide-react';
import { Card, GradientText } from '../../components/ui/index.tsx';
import { buildStep4InsightSections } from '../../constants/step4LivingInsightKnowledge.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

export interface Step4LivingInsightPanelProps {
    factFind: DraftFactFind;
    /** Unused by buildStep4InsightSections (it derives homeowner status from factFind itself) — kept
     *  so the caller (ClientProfile/index.tsx) doesn't need a separate prop shape for this panel. */
    ownsHome?: boolean;
}

const Step4LivingInsightPanel = ({ factFind }: Step4LivingInsightPanelProps) => {
    const sections = buildStep4InsightSections({ factFind });

    return (
        <div className="space-y-4">
            {sections.map((section) => {
                // The land card sits beside the living-arrangement one and is about a different
                // subject, so it does not wear the same house icon.
                const iconKind = (section as { icon?: string }).icon;
                const Icon = iconKind === 'land' ? Trees : iconKind === 'village' ? Building2 : Home;
                const lines = (section as { lines?: { label: string; value: string; total?: boolean }[] }).lines || [];
                return (
                <Card key={section.title} className="bg-white/80">
                    <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
                        <Icon className="text-blue-500 shrink-0" size={20} aria-hidden />
                        <GradientText>{section.title}</GradientText>
                    </h3>
                    {section.subtitle && (
                        <p className="text-sm font-medium text-[#001E7A] mb-3">{section.subtitle}</p>
                    )}
                    {lines.length > 0 && (
                        <dl className="mb-3 divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white/70" data-testid="village-value-lines">
                            {lines.map((line) => (
                                <div key={line.label} className={`flex items-baseline justify-between gap-3 px-3 py-1.5 text-sm ${line.total ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                                    <dt className="leading-snug">{line.label}</dt>
                                    <dd className="shrink-0 tabular-nums">{line.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                    <div className="space-y-3">
                        {section.paragraphs.map((paragraph) => (
                            <p key={paragraph} className="text-gray-600 text-sm leading-relaxed">
                                {paragraph}
                            </p>
                        ))}
                    </div>
                    {section.goalsIntro && (section.goalBlocks?.length ?? 0) > 0 && (
                        <div className="mt-3">
                            <p className="text-gray-600 text-sm leading-relaxed font-medium">
                                {section.goalsIntro}
                            </p>
                            <ul className="mt-2 space-y-2">
                                {(section.goalBlocks ?? []).map(({ label, summary }) => (
                                    <li key={label} className="text-sm text-gray-600 leading-relaxed">
                                        <span className="font-semibold text-gray-800">{label}:</span>{' '}
                                        {summary}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card>
                );
            })}
        </div>
    );
};

export default Step4LivingInsightPanel;
